import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import DefaultLayout from '../components/layouts/DefaultLayout'
import {
  Box,
  Text,
  Container,
  VStack,
  HStack,
  Button,
  Divider,
  Badge,
  Icon,
  Input,
  FormControl,
  FormLabel,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  useToast
} from '@chakra-ui/react'
import { FaLock, FaChartLine, FaExchangeAlt, FaUser } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import { authState } from '../atoms/auth'
import CheckAuth from '../components/layouts/CheckAuth'
import { useEffect, useRef, useState } from 'react'
import BigNumber from 'bignumber.js'
import { getPosition, updatePassword } from '../services/apiService'

const MyPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [auth] = useRecoilState(authState)
  const toast = useToast()

  // 비밀번호 변경 다이얼로그 관련 상태
  const { isOpen, onOpen, onClose } = useDisclosure()
  const cancelRef = useRef(null)
  const [currentPw, setCurrentPw] = useState('')
  const [newPw, setNewPw] = useState('')
  const [confirmPw, setConfirmPw] = useState('')

  // 포지션 상태
  const [position, setPosition] = useState({
    symbol: '',
    uid: '',
    leverage: '',
    entryPrice: '',
    liqPrice: '',
    size: '',
    initialMargin: '',
    value: '',
    realisedPnl: '',
    unrealisedPnl: '',
    calculatedPnl: '',
    pnlPercent: ''
  })

  const handleChangePassword = async () => {
    if (newPw !== confirmPw) {
      toast({
        title: t('비밀번호 변경'),
        description: t('새 비밀번호와 새 비밀번호 확인이 일치하지 않습니다.'),
        status: 'info',
        duration: 3000,
        isClosable: true
      })
      return
    }

    try {
      const { data: res } = await updatePassword({
        oldPassword: currentPw,
        newPassword: newPw
      })
      if (res.statusCode === 200) {
        toast({
          title: t('비밀번호 변경'),
          description: t('비밀번호가 변경되었습니다.'),
          status: 'success',
          duration: 3000,
          isClosable: true
        })
        onClose()

        // 폼 초기화
        setCurrentPw('')
        setNewPw('')
        setConfirmPw('')
      }
    } catch (err: any) {
      toast({
        title: t('비밀번호 변경'),
        description:
          err.response?.data?.message || t('알 수 없는 오류가 발생했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    }
  }

  // 포지션 데이터 가져오기
  const fetchPosition = async () => {
    try {
      const { data: res } = await getPosition()
      if (res.statusCode === 200) {
        setPosition(res.data)
      }
    } catch (error) {
      toast({
        title: t('포지션 조회'),
        description: t('포지션 정보를 불러오는 데 실패했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    }
  }

  // useEffect들 중에 위치
  useEffect(() => {
    fetchPosition()

    const interval = setInterval(() => {
      fetchPosition()
    }, 10000)

    return () => clearInterval(interval)
  }, [])

  return (
    <DefaultLayout>
      <CheckAuth />
      <Container maxW="800px" py={10}>
        <VStack align="stretch" spacing={6}>
          <Box>
            <Text fontSize="2xl" fontWeight="bold">
              {t('{{name}}님의 마이페이지', { name: auth.username })}
            </Text>
          </Box>

          {/* 내 정보 섹션 */}
          <Box borderWidth={1} borderRadius="xl" p={5} boxShadow="sm">
            <HStack justifyContent="space-between" mb={3}>
              <Text fontSize="lg" fontWeight="bold">
                {t('👤 내 정보')}
              </Text>
              <Button
                size="sm"
                variant="outline"
                leftIcon={<FaLock />}
                onClick={onOpen}
              >
                {t('비밀번호 변경')}
              </Button>
            </HStack>

            <VStack align="start" spacing={1}>
              <HStack>
                <Text fontWeight="bold">{t('아이디')}:</Text>
                <Text>{auth.account}</Text>
              </HStack>
              <HStack>
                <Text fontWeight="bold">{t('이름')}:</Text>
                <Text>{auth.username}</Text>
              </HStack>
              <HStack>
                <Text fontWeight="bold">{t('전화번호')}:</Text>
                <Text>{auth.phone}</Text>
              </HStack>
              {/* <HStack>
                <Text fontWeight="bold">{t('등급')}:</Text>
                <Text>{auth.grade || t('일반')}</Text>
              </HStack> */}
              <HStack>
                <Text fontWeight="bold">{t('보유 USDT')}:</Text>
                <Text>{new BigNumber(auth.usdtBalance).toFormat(0)} USDT</Text>
              </HStack>
              <HStack>
                <Text fontWeight="bold">{t('보유 포인트')}:</Text>
                <Text color="blue.600">{new BigNumber(auth.pointBalance).toFormat(0)} P</Text>
              </HStack>
              <HStack>
                <Text fontWeight="bold">{t('커뮤니티 닉네임')}:</Text>
                <Text color={auth.nickname ? "green.600" : "red.500"}>
                  {auth.nickname || t('설정 안됨')}
                </Text>
              </HStack>
            </VStack>
          </Box>

          {/* 이용 정보 섹션 */}
          <Box borderWidth={1} borderRadius="xl" p={5} boxShadow="sm">
            <HStack justifyContent="space-between" mb={3}>
              <Text fontSize="lg" fontWeight="bold">
                {t('⏰ 이용 정보')}
              </Text>
            </HStack>

            <VStack align="start" spacing={1}>
              <HStack>
                <Text fontWeight="bold">{t('이용 만료일')}:</Text>
                <Text>
                  {auth.serviceAt
                    ? dayjs(auth.serviceAt).format('YYYY-MM-DD HH:mm')
                    : ''}
                </Text>
              </HStack>
              <HStack>
                <Text fontWeight="bold">{t('레퍼럴 만료일')}:</Text>
                <Text>
                  {auth.referralAt
                    ? dayjs(auth.referralAt).format('YYYY-MM-DD HH:mm')
                    : ''}
                </Text>
              </HStack>
              <HStack>
                <Text fontWeight="bold">{t('구독료 만료일')}:</Text>
                <Text>
                  {auth.subscriptionAt
                    ? dayjs(auth.subscriptionAt).format('YYYY-MM-DD HH:mm')
                    : ''}
                </Text>
              </HStack>
              <HStack>
                <Text fontWeight="bold">{t('가입일')}:</Text>
                <Text>{dayjs(auth.createdAt).format('YYYY-MM-DD HH:mm')}</Text>
              </HStack>
            </VStack>
          </Box>

          {/* 포지션 섹션 */}
          <Box borderWidth={1} borderRadius="xl" p={5} boxShadow="sm">
            <HStack justifyContent="space-between" mb={3}>
              <Text fontSize="lg" fontWeight="bold">
                {t('📊 현재 포지션')}
              </Text>
              <Badge colorScheme="green">{position?.symbol || t('없음')}</Badge>
            </HStack>
            <Divider mb={3} />
            {position?.size ? (
              <VStack align="start" spacing={1}>
                <HStack>
                  <Text fontWeight="bold">UID:</Text>
                  <Text>{position?.uid}</Text>
                </HStack>
                <HStack>
                  <Text fontWeight="bold">{t('레버리지')}:</Text>
                  <Text>x{position?.leverage}</Text>
                </HStack>
                <HStack>
                  <Text fontWeight="bold">{t('평균단가')}:</Text>
                  <Text fontWeight={'bold'}>{position?.entryPrice}</Text>
                </HStack>
                <HStack>
                  <Text fontWeight="bold">{t('청산가')}:</Text>
                  <Text color="orange" fontWeight={'bold'}>
                    {position?.liqPrice}
                  </Text>
                </HStack>

                <HStack>
                  <Text fontWeight="bold">{t('사이즈')}:</Text>
                  <Text>{position?.size}</Text>
                </HStack>

                <HStack>
                  <Text fontWeight="bold">{t('포지션')}:</Text>
                  <Text
                    color={Number(position?.size) > 0 ? 'green.500' : 'red.500'}
                  >
                    {Number(position?.size) > 0 ? 'Long' : 'Short'}
                  </Text>
                </HStack>

                <HStack>
                  <Text fontWeight="bold">{t('마진')}:</Text>
                  <Text>
                    {new BigNumber(position?.initialMargin).toFormat(2)} USDT
                  </Text>
                </HStack>
                <HStack>
                  <Text fontWeight="bold">{t('가치')}:</Text>
                  <Text>{position?.value} USDT</Text>
                </HStack>
                <HStack>
                  <Text fontWeight="bold">{t('실현손익')}:</Text>
                  <Text
                    color={
                      new BigNumber(position?.realisedPnl).gte(0)
                        ? 'green.500'
                        : 'red.500'
                    }
                  >
                    {new BigNumber(position?.realisedPnl).toFormat(2)} USDT
                  </Text>
                </HStack>

                <HStack>
                  <Text fontWeight="bold">{t('미실현손익')}:</Text>
                  <Text
                    color={
                      new BigNumber(position?.unrealisedPnl).gte(0)
                        ? 'green.500'
                        : 'red.500'
                    }
                  >
                    {new BigNumber(position?.unrealisedPnl).toFormat(2)} USDT
                  </Text>
                </HStack>

                <HStack>
                  <Text fontWeight="bold">{t('종료시손익')}:</Text>
                  <Text
                    color={
                      new BigNumber(position?.calculatedPnl).gte(0)
                        ? 'green.500'
                        : 'red.500'
                    }
                  >
                    {new BigNumber(position?.calculatedPnl).toFormat(2)} USDT
                  </Text>
                </HStack>

                <HStack>
                  <Text fontWeight="bold">{t('PnL %')}:</Text>
                  <Text
                    color={
                      Number(position?.pnlPercent) >= 0
                        ? 'green.500'
                        : 'red.500'
                    }
                  >
                    {position?.pnlPercent} %
                  </Text>
                </HStack>
              </VStack>
            ) : (
              <Text color="gray.500">
                {t('현재 보유 중인 포지션이 없습니다.')}
              </Text>
            )}
          </Box>
        </VStack>
      </Container>

      {/* 비밀번호 변경 다이얼로그 */}
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('비밀번호 변경')}
            </AlertDialogHeader>
            <AlertDialogBody>
              <VStack spacing={4}>
                <FormControl>
                  <FormLabel>{t('현재 비밀번호')}</FormLabel>
                  <Input
                    type="password"
                    value={currentPw}
                    onChange={(e) => setCurrentPw(e.target.value)}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('새 비밀번호')}</FormLabel>
                  <Input
                    type="password"
                    value={newPw}
                    onChange={(e) => setNewPw(e.target.value)}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('새 비밀번호 확인')}</FormLabel>
                  <Input
                    type="password"
                    value={confirmPw}
                    onChange={(e) => setConfirmPw(e.target.value)}
                  />
                </FormControl>
              </VStack>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                {t('취소')}
              </Button>
              <Button
                colorScheme="purple"
                onClick={handleChangePassword}
                ml={3}
              >
                {t('변경')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </DefaultLayout>
  )
}

export default MyPage
